import { AlertTriangle, CheckCircle2, FileSearch } from "lucide-react";
import type { CoverageReportV2 } from "@/lib/api";

interface CoverageBadgeProps {
  coverage: CoverageReportV2;
}

const LEVEL_STYLE: Record<
  CoverageReportV2["level"],
  { fg: string; bg: string; label: string; Icon: typeof FileSearch }
> = {
  complete: {
    fg: "var(--block-green)",
    bg: "color-mix(in oklch, var(--block-green) 14%, transparent)",
    label: "Couverture complète",
    Icon: CheckCircle2,
  },
  partial: {
    fg: "var(--alert-orange)",
    bg: "color-mix(in oklch, var(--alert-orange) 14%, transparent)",
    label: "Couverture partielle",
    Icon: FileSearch,
  },
  weak: {
    fg: "var(--block-red)",
    bg: "color-mix(in oklch, var(--block-red) 14%, transparent)",
    label: "Couverture faible",
    Icon: AlertTriangle,
  },
};

/**
 * Rapport de couverture documentaire (détection de gaps). Affiché sous le
 * bloc confiance : niveau global + ratio + liste précise des assertions non
 * sourcées. Dérivé côté backend des claims (`support_level`) — aucune
 * heuristique côté front.
 */
export function CoverageBadge({ coverage }: CoverageBadgeProps) {
  const style = LEVEL_STYLE[coverage.level] ?? LEVEL_STYLE.partial;
  const pct = Math.round(coverage.coverage_ratio * 100);

  return (
    <div
      className="rounded-md border p-3 text-xs space-y-2"
      style={{
        borderColor: "var(--graphite-600)",
        background: "var(--graphite-800)",
      }}
      aria-label="Rapport de couverture documentaire"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="flex items-center gap-1.5 px-2 py-1 rounded"
          style={{ background: style.bg, color: style.fg }}
        >
          <style.Icon size={11} />
          {style.label} ({pct}%)
        </span>
        <span style={{ color: "var(--arbitre-slate-400)" }}>
          {coverage.supported}/{coverage.total_claims} assertion(s) sourcée(s)
        </span>
      </div>

      <p style={{ color: "var(--arbitre-slate-300)", lineHeight: 1.5 }}>
        {coverage.message}
      </p>

      {coverage.unsupported_claim_ids.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span style={{ color: "var(--arbitre-slate-500)" }}>Sans source :</span>
          {coverage.unsupported_claim_ids.map((id) => (
            <code
              key={id}
              className="gsf-mono-proof px-1.5 py-0.5 rounded text-[10px]"
              style={{
                background:
                  "color-mix(in oklch, var(--block-red) 14%, transparent)",
                color: "var(--block-red)",
              }}
            >
              {id}
            </code>
          ))}
        </div>
      )}
    </div>
  );
}
